import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  EyeOff, 
  User, 
  Key, 
  Navigation, 
  CheckCircle, 
  XCircle,
  Info,
  Settings,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Links from '../../Links';
import { hasPermission } from '../../config/permissions';

const PermissionDebug = () => {
  const { user } = useSelector(state => state.user);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    user: true,
    permissions: false,
    navigation: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const checkLinkPermission = (linkItem) => {
    if (!linkItem.permissions) return { allowed: true, reason: 'No permissions required' };
    
    const { 
      module, 
      action, 
      alwaysVisible, 
      restrictToMainVendor, 
      fallbackForMainVendor 
    } = linkItem.permissions;
        
    if (alwaysVisible) return { allowed: true, reason: 'Always visible' };
    
    // Items restricted to main vendor only
    if (restrictToMainVendor && !user?.is_main_vendor) {
      return { allowed: false, reason: 'Restricted to main vendor only' };
    }
    
    // If no specific permissions defined
    if (!module || !action) return { allowed: true, reason: 'No specific permissions required' };
    
    const userPermissions = user?.permissions || {};
    const isMainVendor = user?.is_main_vendor || false;
    
    // Main vendor fallback
    if (fallbackForMainVendor && isMainVendor) {
      return { allowed: true, reason: 'Main vendor fallback access' };
    }
    
    // Check specific permission
    const hasAccess = hasPermission(userPermissions, isMainVendor, module, action);
    return { 
      allowed: hasAccess, 
      reason: hasAccess 
        ? `Has ${action} permission in ${module}` 
        : `Missing ${action} permission in ${module}`
    };
  };

  const getVisibleNavigationItems = () => {
    return Links.map(link => ({
      ...link,
      access: checkLinkPermission(link)
    }));
  };

  const visibleItems = getVisibleNavigationItems();
  const allowedItems = visibleItems.filter(item => item.access.allowed);
  const deniedItems = visibleItems.filter(item => !item.access.allowed);

  if (!isVisible) {
    return (
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsVisible(true)}
        className="fixed right-4 bottom-4 z-50 p-3 text-white bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl"
        title="Show Permission Debug"
      >
        <Settings className="w-5 h-5" />
      </motion.button>
    );
  }

  if (!user) {
    return (
      <div className="p-4 mb-4 bg-red-50 rounded-lg border border-red-200">
        <h3 className="mb-1 text-sm font-medium text-red-800">Debug: No User</h3>
        <p className="text-sm text-red-700">User is not authenticated</p>
      </div>
    );
  }

  const userPermissions = user?.permissions || [];
  const isMainVendor = user?.is_main_vendor || false;
  
  // Check if user has only deal-related permissions
  const dealPermissions = [
    "View_Deals",
    "Create_Offer", 
    "Edit_Offer", 
    "Delete_Offer", 
    "Blast_Deal"
  ];
  
  const otherPermissions = [
    "View_Partners",
    "Create_Partner", 
    "Edit_Partner", 
    "Delete_Partner",
    "Redeem_Offers",
    "Redeem_Edit",
    "Redeem_Delete",
    "View_Reports_Analytics",
    "View_Subscription",
    "Purchase_Subscription",
    "View_Subscription_History",
    "Role_View",
    "Role_Create",
    "Role_Edit",
    "Role_Delete"
  ];
  
  const hasOnlyDealPermissions = userPermissions.length > 0 && 
    userPermissions.every(perm => dealPermissions.includes(perm)) &&
    !userPermissions.some(perm => otherPermissions.includes(perm));

  return (
    <motion.div
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '100%' }}
      className="overflow-y-auto fixed top-0 right-0 bottom-0 z-50 w-96 bg-white border-l border-gray-200 shadow-2xl"
    >
      {/* Header */}
      <div className="sticky top-0 p-4 text-white bg-gradient-to-r from-orange-500 to-red-600 border-b">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <Shield className="w-5 h-5" />
            <h2 className="font-semibold">Permissions</h2>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 rounded-lg transition-colors hover:bg-white/20"
          >
            <EyeOff className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-xs text-purple-100">
          Real-time permission analysis
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* User Information Section */}
        <div className="bg-gray-50 rounded-lg border">
          <button
            onClick={() => toggleSection('user')}
            className="flex justify-between items-center p-3 w-full text-left transition-colors hover:bg-gray-100"
          >
            <div className="flex gap-2 items-center">
              <User className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-gray-800">User Information</span>
            </div>
            {expandedSections.user ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.user && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200"
              >
                <div className="p-3 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-800 truncate">{user?.email || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-600">Brand:</span>
                    <span className="font-medium text-gray-800 truncate">{user?.brand_name || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-600">Account Type:</span>
                    <span className={`font-medium ${user?.is_main_vendor ? 'text-green-600' : 'text-blue-600'}`}>
                      {user?.is_main_vendor ? 'Main Vendor' : 'Team Member'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-600">User ID:</span>
                    <span className="font-medium text-gray-800">{user?.id || 'N/A'}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Permissions Section */}
        {/* <div className="bg-gray-50 rounded-lg border">
          <button
            onClick={() => toggleSection('permissions')}
            className="flex justify-between items-center p-3 w-full text-left transition-colors hover:bg-gray-100"
          >
            <div className="flex gap-2 items-center">
              <Key className="w-4 h-4 text-green-600" />
              <span className="font-medium text-gray-800">Raw Permissions</span>
            </div>
            {expandedSections.permissions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.permissions && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200"
              >
                <div className="p-3">
                  {user?.permissions && Object.keys(user.permissions).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(user.permissions).map(([module, actions]) => (
                        <div key={module} className="p-2 bg-white rounded-lg border">
                          <h4 className="mb-1 text-xs font-medium text-gray-700">
                            {module.replace(/_/g, ' ')}
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {(Array.isArray(actions) ? actions : []).map((action, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs text-green-700 bg-green-100 rounded-full"
                              >
                                {action}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center text-sm text-gray-500">
                      <Info className="w-4 h-4" />
                      <span>No explicit permissions found</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div> */}

        {/* Navigation Access Section */}
        <div className="bg-gray-50 rounded-lg border">
          <button
            onClick={() => toggleSection('navigation')}
            className="flex justify-between items-center p-3 w-full text-left transition-colors hover:bg-gray-100"
          >
            <div className="flex gap-2 items-center">
              <Navigation className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-gray-800">Navigation Access</span>
              <span className="px-2 py-1 text-xs text-purple-600 bg-purple-100 rounded-full">
                {allowedItems.length}/{visibleItems.length}
              </span>
            </div>
            {expandedSections.navigation ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {expandedSections.navigation && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200"
              >
                <div className="p-3 space-y-3">
                  {/* Allowed Items */}
                  <div>
                    <h4 className="flex gap-1 items-center mb-2 text-xs font-medium text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      Accessible ({allowedItems.length})
                    </h4>
                    <div className="space-y-1">
                      {allowedItems.map((item, index) => (
                        <div key={index} className="p-2 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex gap-2 items-center">
                            <div className="flex-shrink-0 text-green-600">
                              {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-green-800 truncate">{item.name}</p>
                              <p className="text-xs text-green-600">{item.access.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Denied Items */}
                  {deniedItems.length > 0 && (
                    <div>
                      <h4 className="flex gap-1 items-center mb-2 text-xs font-medium text-red-700">
                        <XCircle className="w-3 h-3" />
                        Restricted ({deniedItems.length})
                      </h4>
                      <div className="space-y-1">
                        {deniedItems.map((item, index) => (
                          <div key={index} className="p-2 bg-red-50 rounded-lg border border-red-200">
                            <div className="flex gap-2 items-center">
                              <div className="flex-shrink-0 text-red-600">
                                {item.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-red-800 truncate">{item.name}</p>
                                <p className="text-xs text-red-600">{item.access.reason}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Summary Stats */}
        <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
          <h4 className="mb-2 text-sm font-medium text-purple-800">Access Summary</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 text-center bg-white rounded">
              <div className="text-lg font-bold text-green-600">{allowedItems.length}</div>
              <div className="text-gray-600">Accessible</div>
            </div>
            <div className="p-2 text-center bg-white rounded">
              <div className="text-lg font-bold text-red-600">{deniedItems.length}</div>
              <div className="text-gray-600">Restricted</div>
            </div>
          </div>
        </div>

        {/* <div className="p-4 mt-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="mb-2 text-sm font-medium text-gray-800">Debug: User Permissions</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">User ID:</span> {user.id}
            </div>
            <div>
              <span className="font-medium">Role:</span> {user.role}
            </div>
            <div>
              <span className="font-medium">Is Main Vendor:</span> {isMainVendor ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="font-medium">Permissions:</span>
              <div className="mt-1 ml-4">
                {Array.isArray(userPermissions) ? (
                  <div>
                    <div className="mb-1 text-xs text-gray-600">Array format:</div>
                    <div className="p-2 bg-white rounded border">
                      {userPermissions.length > 0 ? (
                        userPermissions.map((perm, index) => (
                          <div key={index} className="font-mono text-xs">
                            "{perm}"
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-500">No permissions</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 text-xs text-gray-600">Object format:</div>
                    <div className="p-2 bg-white rounded border">
                      <pre className="overflow-auto text-xs">
                        {JSON.stringify(userPermissions, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
                    {hasOnlyDealPermissions && (
          <div className="p-2 mt-2 bg-blue-100 rounded border border-blue-200">
            <div className="text-xs font-medium text-blue-800">⚠️ Limited Access User</div>
            <div className="text-xs text-blue-700">This user has only deal-related permissions and will be restricted to deal management features only.</div>
          </div>
        )}
          </div>
        </div> */}
      </div>
    </motion.div>
  );
};

export default PermissionDebug; 